import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import type { TranslationKey } from '../../i18n';

const NewsletterForm: React.FC = () => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [errorMsg, setErrorMsg] = useState('');
    const { language, t } = useLanguage();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('loading');
        setErrorMsg('');
        try {
            const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
            await axios.post(`${baseUrl}/api/newsletter`, {
                email,
                language: language === 'PT' ? 'PT' : language === 'ES' ? 'ES' : 'EN',
            });
            setStatus('success');
        } catch (err: any) {
            setStatus('error');
            const apiError = err?.response?.data?.error;
            const fieldErrors = apiError?.fields
                ? Object.values(apiError.fields as Record<string, string>).join('. ')
                : null;
            setErrorMsg(
                fieldErrors
                ?? apiError?.message
                ?? 'Something went wrong. Please try again.'
            );
        }
    };

    if (status === 'success') {
        return (
            <div className="nl-success">
                <svg viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span>{t('newsletter.success' as TranslationKey)}</span>
            </div>
        );
    }

    return (
        <form className="nl-form" onSubmit={handleSubmit}>
            <div className="nl-input-row">
                <input
                    type="email"
                    className="nl-input"
                    placeholder={t('newsletter.placeholder' as TranslationKey)}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={status === 'loading'}
                />
                <button type="submit" className="nl-btn" disabled={status === 'loading'}>
                    {status === 'loading'
                        ? t('newsletter.submitting' as TranslationKey)
                        : t('newsletter.cta' as TranslationKey)}
                </button>
            </div>
            {status === 'error' && (
                <p className="nl-error">{errorMsg}</p>
            )}
            <p className="nl-privacy">{t('newsletter.privacy' as TranslationKey)}</p>
        </form>
    );
};

export default NewsletterForm;
